import { useState } from 'react';
import { Button } from '@citibox-panel/ui';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { isAnonymized } from '../../../lib/isAnonymized';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { accountTypeClass, isCourierAccount } from '../lib/accountType';
import { DeleteAccountDialog } from './DeleteAccountDialog';

export interface DeleteAccountSectionProps {
  account: UserSearchHit;
  phoneKey: string;
  deleteUser: (id: string, amazonExclusion: boolean, reason?: string) => Promise<void>;
}

export function DeleteAccountSection({ account, phoneKey, deleteUser }: DeleteAccountSectionProps) {
  const [open, setOpen] = useState(false);
  // Called unconditionally, ahead of the anonymized gate below: this same component instance can
  // be re-rendered mid-mount with a status that just flipped (the Risk-2 race), and the Rules of
  // Hooks forbid skipping a hook call on some renders but not others.
  const mutation = useDeleteUser(
    account.id,
    phoneKey,
    deleteUser,
    accountTypeClass(account.accountType),
  );

  if (isAnonymized(account.status)) return null;

  return (
    <>
      {/* TODO: i18n — copy en ES hasta Fase 1. */}
      <Button
        variant="outlined"
        color="error"
        // `small` + `bold` to match the card's other actions (design pass 2026-09-11, CBX-11387):
        // «Guardar» and «Cancelar» sit on the status row at this size, and this button shares the
        // card with them while editing — the baja is no longer hidden during a correction.
        size="small"
        sentenceCase
        bold
        onClick={() => {
          setOpen(true);
        }}
      >
        Dar de baja
      </Button>
      <DeleteAccountDialog
        open={open}
        // The dialog already refuses to close mid-flight (✕/Cancelar go disabled, Escape and the
        // backdrop are refused) — so by the time this can fire, there is nothing in flight left to
        // guard here.
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={(amazonExclusion, reason) => {
          const vars = reason === undefined ? { amazonExclusion } : { amazonExclusion, reason };
          mutation.mutate(vars, {
            // Per-call, not the hook's own onSuccess: this one runs strictly AFTER TanStack Query
            // has awaited the hook-level onSuccess to completion (confirmed against
            // @tanstack/query-core's Mutation#execute — it dispatches the 'success' state, which is
            // what flips isPending, only once that promise settles). useDeleteUser's onSuccess
            // chains the invalidation-and-refetch, so by the time this fires the mutation has
            // SETTLED, not merely resolved the write — closing here is what keeps the agent from
            // ever seeing the pre-baja ficha behind the dialog.
            onSuccess: () => {
              setOpen(false);
            },
          });
        }}
        isPending={mutation.isPending}
        isError={mutation.isError}
        cause={mutation.error}
        // Presentation only: an account with no Amazon history is never offered the exclusion. The
        // write does not revalidate this, so the flag gates the section's visibility, nothing more.
        showAmazonExclusion={account.hasAmazonDeliveries}
        courier={isCourierAccount(account.accountType)}
      />
    </>
  );
}
